/**
 * The countdown a player reads while the game they are in gets its connection back.
 *
 * The lobby has had one of these since reconnect landed. This is the same wait over a running
 * mode: the world holds on the last frame that arrived, this sits over it, and when the host
 * answers it goes away and the game carries on from the next keyframe. Nothing is torn down and
 * nothing is loaded again, which is the whole point of
 * [issue 121](https://github.com/Jonahenk/TBD/issues/121).
 *
 * The shell owns it rather than a mode, for the same reason it owns the notices: the transition a
 * lost session produces belongs to whatever is showing, and a mode that knew about it would have to
 * be written again in the second mode and the third.
 *
 * It takes the pointer back, which the notices deliberately do not. A notice is read past; this is
 * acted on, and a button under a pointer the game has locked is a button nobody can press.
 */

import { LOBBY_STOP_WAITING } from '@/text/lobbyText';
import { el, setText } from '@/ui/dom';
import { injectReconnectOverlayStyles } from '@/ui/net/reconnectOverlayStyles';
import { rejoiningLine } from '@/net/lobby/lobbyNotices';

export interface ReconnectOverlay {
  /** The countdown, in milliseconds left of the budget. */
  show(msLeft: number): void;
  dispose(): void;
}

export function createReconnectOverlay(
  container: HTMLElement,
  onCancel: () => void,
): ReconnectOverlay {
  const removeStyles = injectReconnectOverlayStyles();
  const root = el('div', 'reconnect');
  root.setAttribute('role', 'status');
  root.setAttribute('aria-live', 'polite');

  const line = el('p', 'reconnect-line', '');
  const cancel = el('button', 'reconnect-quiet', LOBBY_STOP_WAITING);
  cancel.type = 'button';
  cancel.addEventListener('click', onCancel);

  root.append(line, cancel);
  container.append(root);
  // The mouse belongs to the game while the game is being played, and this is the one moment it is
  // not. Without giving the pointer back, the button under here is a button nobody can press.
  if (document.pointerLockElement !== null) document.exitPointerLock();

  return {
    show(msLeft: number): void {
      setText(line, rejoiningLine(msLeft));
    },

    dispose(): void {
      root.remove();
      removeStyles();
    },
  };
}
